import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { TFunction } from "i18next";
import { SearchOutlined } from "@ant-design/icons";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormBaseDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  SurveyStatisticsFiltersProps,
  SurveyStatisticsFiltersState,
} from "../types";

const surveyInviteFiltersSchema = (
  t: TFunction,
): yup.ObjectSchema<SurveyStatisticsFiltersState> =>
  yup
    .object()
    .shape({
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .typeError(t("form.validation.notValid")),
      isSurveyStarted: yup.boolean().typeError(t("form.validation.notValid")),
      isSubmissionRequired: yup
        .boolean()
        .typeError(t("form.validation.notValid")),
      isSubmitted: yup.boolean().typeError(t("form.validation.notValid")),
      survey: yup.number().typeError(t("form.validation.notValid")),
    })
    .required();

const SurveyStatisticsFilters = ({
  filterNames,
  setFilters,
  defaultFilters = {},
}: SurveyStatisticsFiltersProps) => {
  const { t } = useTranslation();

  const form = useForm<SurveyStatisticsFiltersState>({
    resolver: yupResolver(surveyInviteFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: undefined,
      isSurveyStarted: undefined,
      isSubmissionRequired: undefined,
      isSubmitted: undefined,
      ...defaultFilters,
    },
  });

  const booleanOptions = [
    { label: t("survey.labels.yes"), value: "true" },
    { label: t("survey.labels.no"), value: "false" },
  ];

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            suffix={<SearchOutlined />}
            placeholder={t("survey.placeholders.searchOrganizations")}
          />
        ),
      },
      isSurveyStarted: {
        name: "isSurveyStarted",
        label: t("survey.labels.isSurveyStarted"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="isSurveyStarted"
            control={form.control}
            options={booleanOptions}
            placeholder={t("survey.placeholders.selectStatus")}
            allowClear={true}
          />
        ),
      },
      isSubmissionRequired: {
        name: "isSubmissionRequired",
        label: t("survey.labels.isSubmissionRequired"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="isSubmissionRequired"
            control={form.control}
            options={booleanOptions}
            placeholder={t("survey.placeholders.selectStatus")}
            allowClear={true}
          />
        ),
      },
      isSubmitted: {
        name: "isSubmitted",
        label: t("survey.labels.isSubmitted"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="isSubmitted"
            control={form.control}
            options={booleanOptions}
            placeholder={t("survey.placeholders.selectStatus")}
            allowClear={true}
          />
        ),
      },
    };
  }, []);

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("survey.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters?.(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      handleSearch={() => setFilters?.(form.getValues())}
      className="filter-layout"
    />
  );
};

export default SurveyStatisticsFilters;
